import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import { FunnelIcon } from '../ui/SortIcons';

/**
 * Column-header date-range filter dropdown.
 *
 * Sibling to ColumnFilterDropdown: same funnel affordance and
 * click-outside-to-close behaviour, but the popover holds two date pickers
 * (From / To) for an inclusive Published Date range. Either bound is optional.
 *
 * `from`/`to` are ISO `YYYY-MM-DD` strings (or '') and are emitted via
 * `onChange(from, to)`. Picking a date only updates local draft state — the
 * parent is not notified (and therefore does not refetch) until the user hits
 * Apply. This keeps the popover open while both bounds are chosen instead of
 * triggering a refetch/re-render on every pick.
 */

// Parse an ISO YYYY-MM-DD string into a local-midnight Date for the picker.
// Building from parts (rather than `new Date(str)`, which parses as UTC) keeps
// the calendar on the intended day regardless of the viewer's timezone.
function isoToDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Format a Date back to ISO YYYY-MM-DD using local parts (no UTC shift).
function dateToIso(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function DateRangeFilterDropdown({ from = '', to = '', onChange, active }) {
  const [open, setOpen] = useState(false);
  // Draft bounds held locally so picking a date doesn't notify the parent
  // (and trigger a refetch) until Apply is clicked.
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const containerRef = useRef(null);

  // Re-seed the draft from props whenever the popover is opened, so it always
  // reflects the currently-applied filter (including external clears/changes).
  useEffect(() => {
    if (open) {
      setDraftFrom(from);
      setDraftTo(to);
    }
  }, [open, from, to]);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(e) {
      // Ignore clicks inside the popover and inside the react-datepicker
      // calendar popup (which may render outside the container).
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        !e.target.closest?.('.react-datepicker')
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const fromDate = isoToDate(draftFrom);
  const toDate = isoToDate(draftTo);

  const pickerClass =
    'h-8 w-full rounded-md border border-border bg-background px-2 text-sm ' +
    'text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`flex items-center justify-center py-1 px-0.5 rounded transition-colors hover:bg-muted-foreground/10 text-muted-foreground ${active ? 'text-primary' : ''}`}
        aria-label="Filter by published date"
        aria-expanded={open}
        data-testid="column-filter-button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <FunnelIcon className={`h-2.5 w-2.5 ${active ? 'opacity-100' : 'opacity-[0.35]'}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-56 rounded-md bg-card text-card-foreground shadow-lg z-50 border border-border p-3 font-normal"
          data-testid="date-range-filter-dropdown"
          // Stop header click handlers (sort toggles, row selection) from firing
          // when interacting with the calendar.
          onClick={(e) => e.stopPropagation()}
        >
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            From
          </label>
          <div data-testid="date-range-from">
            <DatePicker
              selected={fromDate}
              onChange={(date) => setDraftFrom(dateToIso(date))}
              selectsStart
              startDate={fromDate}
              endDate={toDate}
              maxDate={toDate || undefined}
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              isClearable
              className={pickerClass}
              wrapperClassName="block w-full"
            />
          </div>
          <label className="block text-xs font-medium text-muted-foreground mt-3 mb-1">
            To
          </label>
          <div data-testid="date-range-to">
            <DatePicker
              selected={toDate}
              onChange={(date) => setDraftTo(dateToIso(date))}
              selectsEnd
              startDate={fromDate}
              endDate={toDate}
              minDate={fromDate || undefined}
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              isClearable
              className={pickerClass}
              wrapperClassName="block w-full"
            />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
              disabled={!draftFrom && !draftTo}
              data-testid="date-range-clear"
              onClick={() => {
                setDraftFrom('');
                setDraftTo('');
                onChange('', '');
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              data-testid="date-range-apply"
              onClick={() => {
                onChange(draftFrom, draftTo);
                setOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { DateRangeFilterDropdown };
